import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'
import TicketIcon from '../../assets/icons/svg/TicketIcon.svg'
import { useAtomValue, useSetAtom } from 'jotai'
import {
    cartState,
    countryState,
    decreaseCartItem,
    increaseCartItem,
    languageState,
    updateCart,
    LanguageHelper,
    NumberHelpers,
    type TProduct
} from '@repo/unicity'
import QtyInput from './qtyInput/QtyInput'
import type { TProductCredit } from '@repo/unicity'
import { useCallback, useEffect, useMemo } from 'react'

const Cart = ({ productCredits, products }: { productCredits: TProductCredit | null; products: TProduct[] }) => {
    const { translate } = useTranslate()
    const country = useAtomValue(countryState)
    const language = useAtomValue(languageState)
    const increase = useSetAtom(increaseCartItem)
    const decrease = useSetAtom(decreaseCartItem)
    const update = useSetAtom(updateCart)
    const cart = useAtomValue(cartState)

    // Memoize cart total to avoid recalculating on every render
    const cartTotal = useMemo(() => cart.reduce((acc, item) => acc + item.product.price * item.quantity, 0), [cart])

    // Memoize product credit calculation
    const productCreditAmount = useMemo(() => {
        if (productCredits && productCredits.availableBalance > 0) {
            return productCredits.availableBalance >= cartTotal ? cartTotal : productCredits.availableBalance
        }
        return 0
    }, [productCredits, cartTotal])

    // Stable callback references using useCallback
    const increaseQuantity = useCallback(
        (item: TProduct) => {
            const cartItem = cart.find(cartItem => cartItem.product.item_code === item.item_code)
            if (cartItem?.quantity === Number(item.qty_sellable) || Number(cartItem?.quantity) >= 2) {
                return
            }
            increase({ product: item, quantity: 1 })
        },
        [cart, increase]
    )

    const decreaseQuantity = useCallback(
        (item: TProduct) => {
            const cartItem = cart.find(cartItem => cartItem.product.item_code === item.item_code)
            if (cartItem?.quantity === 1) {
                return
            }
            decrease({ product: item, quantity: 1 }, false)
        },
        [cart, decrease]
    )

    const updateQuantity = useCallback(
        (item: TProduct, quantity: number) => {
            if (quantity > 2) {
                return
            }
            update({ product: item, quantity: quantity })
        },
        [update]
    )

    useEffect(() => {
        if (cart.length === 0 && products.length > 0) {
            products.forEach(item => {
                update({
                    product: item,
                    quantity: 1
                })
            })
        }
    }, [cart.length, products.length])

    return (
        <Card
            sx={{
                '&.MuiPaper-root': {
                    boxSizing: 'border-box',
                    padding: {
                        xs: '16px',
                        sm: '24px'
                    }
                }
            }}>
            <Typography variant="cardTitle" color={'#003B6F'}>
                {translate('shopping_cart')}
            </Typography>
            <Divider sx={{ margin: '16px 0px' }} />
            <CardContent>
                <Stack direction={'row'} alignItems={'center'} gap={'8px'}>
                    {cart.length > 0 &&
                        products.map(item => {
                            const cartItem = cart.find(cartItem => cartItem.product.item_code === item.item_code)
                            return (
                                <Stack direction={'row'} gap={'15px'} width={'100%'} key={item.item_code}>
                                    <img src={TicketIcon} alt="ticket" />
                                    <Stack direction={'row'} gap={'15px'} justifyContent={'space-between'} width={'100%'}>
                                        <Stack gap={'4px'}>
                                            <Typography
                                                variant="productTitle"
                                                color={'#003B6F'}
                                                sx={{
                                                    fontSize: {
                                                        xs: '14px',
                                                        sm: '16px'
                                                    }
                                                }}>
                                                {LanguageHelper.selectEnglishOrNative(item.item_name, language)}
                                            </Typography>
                                            <Typography variant="muted" fontSize={'12px'} color={'rgba(51, 98, 140, 1)'}>
                                                Code: {item.item_code}
                                            </Typography>
                                        </Stack>
                                        <Stack justifyContent={'flex-start'} alignItems={'flex-end'} gap={'4px'}>
                                            <Typography
                                                variant="productTitle"
                                                color={'#003B6F'}
                                                sx={{
                                                    fontSize: {
                                                        xs: '14px',
                                                        sm: '16px'
                                                    }
                                                }}>
                                                {NumberHelpers.formatNumber(item.price)} {country?.config.currency.code}
                                            </Typography>
                                            <Stack width={'65px'}>
                                                <QtyInput
                                                    quantity={cartItem?.quantity || 1}
                                                    setQuantity={quantity => updateQuantity(item, quantity)}
                                                    increase={() => increaseQuantity(item)}
                                                    decrease={() => decreaseQuantity(item)}
                                                />
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            )
                        })}
                </Stack>
                <Divider sx={{ margin: '16px 0px' }} />
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                    <Typography variant="productSubTotal" color={'#003B6F'}>
                        {translate('subtotal')}
                    </Typography>
                    <Typography variant="productSubTotal" color={'#003B6F'}>
                        {NumberHelpers.formatNumber(cartTotal)} {country?.config.currency.code}
                    </Typography>
                </Stack>
                {productCredits && productCredits.availableBalance > 0 && (
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant="productSubTotal" color={'#003B6F'}>
                            {translate('product_credit')}
                        </Typography>
                        <Typography variant="productSubTotal" color={'rgba(111, 207, 151, 1)'}>
                            -{NumberHelpers.formatNumber(productCreditAmount)} {country?.config.currency.code}
                        </Typography>
                    </Stack>
                )}
                <Divider sx={{ margin: '16px 0px' }} />
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                    <Typography variant="productTotal" color={'#003B6F'} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                        {translate('total_price')}:
                    </Typography>
                    <Typography variant="productTotal" color={'#003B6F'} fontFamily={'Inter'} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                        {NumberHelpers.formatNumber(cartTotal - productCreditAmount)} {country?.config.currency.code}
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    )
}

export default Cart
